import { CardContent } from '@mui/material'
import React from 'react'
import { z } from 'zod'

import { SchemaContact, SchemaWatemplate } from '@/__generated/es-api'
import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import { Form } from '@/app/component/form'
import { ActionsContainer } from '@/app/component/form/common'
import { FormFieldProps } from '@/app/component/form/field'
import SubmitButton from '@/app/component/form/submit-button'
import { extractWhatsappContent } from '@/app/module/common/whatsapp/whatsapp-content'
import { useRegisterWaTemplate } from '@/app/module/content/hooks/use-register-wa-template'
import WhatsappRowContent from '@/app/module/logs/component/whatsapp-row-content'
import { TWhatsappContent } from '@/app/types'

type Props = {
  open: boolean
  template: SchemaWatemplate
  onClose: () => void
}

const ReRegisterDialog: React.FC<Props> = ({ open, onClose, template }) => {
  const { used } = template
  const waContent = React.useMemo(
    () => extractWhatsappContent(template.content as TWhatsappContent),
    [template.content],
  )

  const { schema, fields } = React.useMemo(() => {
    const shape: Record<string, z.ZodTypeAny> = {
      category: z.enum(['MARKETING', 'UTILITY']),
    }

    const localFields: Record<string, FormFieldProps<any>> = {
      content_title: {
        type: 'title',
        fieldProps: {
          name: 'content_title',
          variant: 'subtitle2',
          gutterBottom: true,
          color: 'textSecondary',
          children: 'Actual Content',
        },
      },
      content_body: {
        type: 'title',
        fieldProps: {
          name: 'content_body',
          component: 'div',
          variant: 'body2',
          children: <WhatsappRowContent waContent={waContent as any} />,
          sx: { mb: 2 },
        } as any,
      },
      category: {
        type: 'radio',
        fieldProps: {
          name: 'category',
          label: 'Category',
          options: [
            { label: 'Marketing', value: 'MARKETING' },
            { label: 'Utility', value: 'UTILITY' },
          ],
        },
      },
      divider: {
        type: 'divider',
        fieldProps: { name: 'divider', sx: { my: 2 } },
      },
      examples_title: {
        type: 'title',
        fieldProps: {
          name: 'examples_title',
          variant: 'h6',
          gutterBottom: true,
          children: 'Examples',
        },
      },
      examples_body: {
        type: 'title',
        fieldProps: {
          name: 'examples_body',
          variant: 'body2',
          color: 'text.secondary',
          paragraph: true,
          children:
            'Please provide example values for the variables used in this template. These values will be sent to Meta for approval.',
        },
      },
    }

    let hasDynamicFields = false

    if (used?.contactId) {
      shape.id = z.coerce.number().optional()
      localFields.id = {
        type: 'number',
        fieldProps: { name: 'id', label: 'Contact ID' },
      }
      hasDynamicFields = true
    }
    if (used?.contactFirstName) {
      shape.firstName = z.string().optional()
      localFields.firstName = {
        type: 'text',
        fieldProps: { name: 'firstName', label: 'First Name' },
      }
      hasDynamicFields = true
    }
    if (used?.contactLastName) {
      shape.lastName = z.string().optional()
      localFields.lastName = {
        type: 'text',
        fieldProps: { name: 'lastName', label: 'Last Name' },
      }
      hasDynamicFields = true
    }
    if (used?.contactFullPhoneNumber) {
      shape.fullPhoneNumber = z.string().optional()
      localFields.fullPhoneNumber = {
        type: 'text',
        fieldProps: { name: 'fullPhoneNumber', label: 'Phone Number' },
      }
      hasDynamicFields = true
    }
    if (used?.contactLanguage) {
      shape.language = z.string().optional()
      localFields.language = {
        type: 'text',
        fieldProps: { name: 'language', label: 'Language' },
      }
      hasDynamicFields = true
    }
    if (used?.contactCreated) {
      shape.createdAt = z.string().optional()
      localFields.createdAt = {
        type: 'text',
        fieldProps: { name: 'createdAt', label: 'Created At' },
      }
      hasDynamicFields = true
    }

    if (used?.customFieldIds) {
      Object.entries(used.customFieldIds).forEach(([id]) => {
        const key = `customFields_${id}`
        shape[key] = z.string().optional()
        localFields[key] = {
          type: 'text',
          fieldProps: {
            name: key,
            label: `Custom Field ${id}`,
          },
        }
        hasDynamicFields = true
      })
    }

    if (!hasDynamicFields) {
      localFields.examples_body = {
        type: 'title',
        fieldProps: {
          name: 'no_examples',
          variant: 'body2',
          color: 'text.secondary',
          sx: { fontStyle: 'italic' },
          children: 'No example fields required for this template.',
        },
      }
    }

    return { schema: z.object(shape), fields: localFields }
  }, [used, waContent])

  const [registerTemplate, { isLoading }] = useRegisterWaTemplate()

  const onSubmit = async (data: Record<string, any>) => {
    const exampleContact: SchemaContact = {
      id: data.id,
      firstName: data.firstName,
      lastName: data.lastName,
      fullPhoneNumber: data.fullPhoneNumber,
      language: data.language,
    }

    if (used?.customFieldIds) {
      exampleContact.customFields = Object.keys(used.customFieldIds).reduce(
        (acc, id) => {
          const value = data[`customFields_${id}`]

          if (value) {
            acc[id] = value
          }

          return acc
        },
        {} as Record<string, string>,
      )
    }

    const success = await registerTemplate(template.id, {
      category: data.category,
      exampleContact,
    })

    if (success) {
      onClose()
    }
  }

  return (
    <Dialog open={open} fullWidth onClose={onClose}>
      <DialogHeader icon="app_registration" title="Re-register Template" onClose={onClose} />
      <CardContent>
        <Form
          schema={schema}
          fields={fields as any}
          defaultValues={{ category: template.category }}
          onSubmit={onSubmit}
          renderActions={({ isDirty }) => (
            <ActionsContainer>
              <SubmitButton isDirty={isDirty} isSubmitting={isLoading}>
                Re-register
              </SubmitButton>
            </ActionsContainer>
          )}
        />
      </CardContent>
    </Dialog>
  )
}

export default ReRegisterDialog
